import type { Article } from './articleTypes';

export const financements: Article = {
  slug: 'achat-credit-loa-lld',
  category: 'Financement',
  title: 'Achat comptant, crédit, LOA ou LLD : comment comparer ?',
  seoTitle: 'Achat, crédit, LOA ou LLD : comparaison complète',
  description:
    'Comparer achat comptant, crédit auto, LOA et LLD avec le coût total, la propriété, la valeur finale, le kilométrage et les frais de restitution.',
  intro:
    'Une mensualité ne dit ni ce que la voiture coûte, ni ce qu’il vous reste à la fin. Pour comparer correctement quatre modes d’acquisition, il faut aligner durée, kilométrage, apport, services et situation finale.',
  image: '/assets/guides/guide-financements.webp',
  imageAlt:
    'Voiture vue du dessus face à quatre trajectoires de couleurs différentes',
  publishedAt: '2026-07-17',
  updatedAt: '2026-07-17',
  readingTime: '15 min',
  takeaways: [
    'Le crédit et l’achat comptant vous laissent une voiture ; une LOA rendue ou une LLD vous laissent uniquement l’usage passé. Les totaux ne sont pas comparables sans valeur finale.',
    'Dans une location, distinguez premier loyer majoré et dépôt de garantie : le premier n’est généralement pas restitué.',
    'Demandez le coût total en euros, le kilométrage contractuel, le prix de rachat et le barème de restitution avant de regarder la mensualité.',
  ],
  sections: [
    {
      id: 'definitions',
      title: 'Ce que vous achetez dans chaque formule',
      content: (
        <>
          <div className="as-table-scroll" role="region" aria-label="Comparaison achat crédit LOA LLD" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>Formule</th>
                  <th>Propriétaire pendant le contrat</th>
                  <th>Fin normale</th>
                  <th>Variable décisive</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Achat comptant</th>
                  <td>Vous</td>
                  <td>Vous gardez ou revendez la voiture.</td>
                  <td>Trésorerie immobilisée et valeur future.</td>
                </tr>
                <tr>
                  <th>Crédit auto</th>
                  <td>Vous, sous réserve des garanties du contrat</td>
                  <td>Vous gardez la voiture une fois le prêt remboursé.</td>
                  <td>TAEG, coût total, assurance et durée.</td>
                </tr>
                <tr>
                  <th>LOA</th>
                  <td>Le bailleur</td>
                  <td>Vous rendez ou achetez au prix fixé au contrat.</td>
                  <td>Premier loyer, loyers, option d’achat et restitution.</td>
                </tr>
                <tr>
                  <th>LLD</th>
                  <td>Le loueur</td>
                  <td>Vous rendez la voiture, sans option d’achat contractuelle.</td>
                  <td>Forfait kilométrique, services et frais de restitution.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            La DGCCRF définit la LOA comme une location assortie de la faculté d’acheter le véhicule à
            un prix prévu dès le départ. La LLD reste une location sans cette option. Un concessionnaire
            peut présenter les deux sous le mot « leasing » : je reviens toujours au contrat exact.
          </p>
          <aside className="as-article-note as-note-fact">
            <strong>Cadre de cette comparaison</strong>
            <p>
              Il s’agit d’une méthode de lecture automobile, pas d’un conseil financier individualisé.
              Auto Sens ne recherche, ne négocie ni ne commercialise de financement et ne recommande
              aucun établissement. Pour une décision engageant votre situation financière, faites valider
              les données contractuelles par un interlocuteur habilité.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'comptant',
      title: 'Achat comptant : simple à lire, pas gratuit pour autant',
      content: (
        <>
          <p>
            L’achat comptant évite intérêts et frais de financement. Vous pouvez vendre la voiture quand
            vous le souhaitez et vous n’avez ni kilométrage contractuel, ni état de restitution à négocier.
            Le coût automobile reste pourtant réel : dépréciation, assurance, entretien, énergie et pneus
            continuent de courir.
          </p>
          <p>
            Il existe aussi un coût moins visible : l’argent mobilisé n’est plus disponible pour une
            épargne de précaution ou un autre projet. Je ne fixe pas la « bonne » réserve de trésorerie à
            la place du client. Je vérifie simplement que l’achat ne transforme pas une voiture raisonnable
            en tension de liquidité.
          </p>
          <aside className="as-article-note as-note-opinion">
            <strong>Quand cette formule me paraît lisible</strong>
            <p>
              Vous comptez garder la voiture, le paiement laisse une marge de sécurité confortable et vous
              privilégiez la liberté de revendre sans solder un contrat. Ce constat ne suffit pas à dire que
              le comptant est financièrement optimal pour votre situation personnelle.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'credit',
      title: 'Crédit : comparer le TAEG et le total dû, pas le taux d’appel',
      content: (
        <>
          <p>
            Un crédit permet d’être propriétaire tout en étalant le paiement. Le contrat doit notamment
            indiquer le montant du crédit, sa durée, le calendrier des remboursements, le montant total dû,
            le coût des frais et le TAEG. Service-Public rappelle que le TAEG intègre le coût du crédit selon
            les règles applicables et que le consommateur dispose en principe de quatorze jours calendaires
            pour se rétracter après la signature.
          </p>
          <p>
            J’aligne les offres sur la même somme empruntée et la même durée. Un taux plus bas peut masquer
            des frais de dossier ou une assurance plus coûteuse ; une mensualité plus faible peut simplement
            prolonger la dette. Si l’assurance est facultative, je calcule le total avec et sans elle, puis
            je note explicitement le niveau de protection associé.
          </p>
          <p>
            Le crédit affecté est lié à l’achat désigné, quand un prêt personnel n’est pas attaché à un bien
            précis. Cette différence peut avoir des conséquences si la vente ne se réalise pas. Le contrat
            remis par le prêteur, et non l’intitulé commercial de l’offre, doit guider la lecture.
          </p>
        </>
      ),
    },
    {
      id: 'loa',
      title: 'LOA : deux calculs, selon que vous rendez ou achetez',
      content: (
        <>
          <p>
            Une LOA ne se résume pas à « apport + mensualités ». Il faut distinguer le premier loyer majoré,
            qui n’est en principe pas restitué, d’un éventuel dépôt de garantie, dont les conditions de
            restitution figurent au contrat. Il faut ensuite ajouter tous les loyers et, si vous comptez
            garder la voiture, le prix de l’option d’achat.
          </p>
          <div className="as-formula-grid">
            <div>
              <strong>LOA rendue</strong>
              <code>1er loyer + loyers + frais + restitution éventuelle</code>
            </div>
            <div>
              <strong>LOA rachetée</strong>
              <code>1er loyer + loyers + frais + option d’achat</code>
            </div>
          </div>
          <p>
            La DGCCRF précise que le contrat doit notamment mentionner le prix comptant du bien, la durée,
            les loyers, la somme restant à payer en cas d’achat et les conséquences d’une défaillance. La LOA
            bénéficie d’un délai de rétractation de quatorze jours à compter de l’acceptation du contrat.
            Contrairement à un crédit classique, le TAEG n’est pas l’indicateur prévu pour la LOA : la
            comparaison en euros devient d’autant plus importante.
          </p>
          <p>
            Si vous pensez rendre la voiture, je lis avant signature le forfait kilométrique, le prix du
            kilomètre supplémentaire, les standards d’usure, les frais de remise en état et la procédure
            contradictoire. Ces coûts sont incertains au départ, mais les ignorer ne les rend pas nuls.
          </p>
        </>
      ),
    },
    {
      id: 'lld',
      title: 'LLD : payer un usage défini, sans valeur finale',
      content: (
        <>
          <p>
            La LLD convient à celui qui veut utiliser une voiture pendant une durée et un kilométrage
            déterminés, puis la rendre. Certains contrats incluent entretien, assistance ou pneus ; d’autres
            non. Deux loyers identiques peuvent donc acheter des services très différents.
          </p>
          <p>
            Sa lisibilité dépend de votre capacité à prévoir les kilomètres. Un contrat à 10 000 km par an
            n’est pas moins cher si vous en parcourez 18 000 et payez le dépassement. À l’inverse, les
            kilomètres non parcourus ne sont pas nécessairement remboursés. Les conditions de sortie avant
            terme peuvent également être coûteuses.
          </p>
          <p>
            À la fin, vous n’avez pas d’actif à revendre. Ce n’est ni un vice caché ni un avantage automatique :
            c’est la nature du contrat. La comparaison honnête consiste à mettre face à face le coût d’usage
            de la LLD et le coût de détention net de la valeur de revente pour un achat.
          </p>
        </>
      ),
    },
    {
      id: 'exemple',
      title: 'Exemple chiffré : pourquoi la mensualité trompe',
      content: (
        <>
          <p>
            Prenons une voiture affichée 25 000 €, sur 48 mois et 60 000 km. Les chiffres ci-dessous sont
            <strong> entièrement fictifs</strong> et n’intègrent ni entretien, ni assurance, ni énergie,
            ni frais de restitution. Ils servent seulement à montrer la méthode.
          </p>
          <div className="as-table-scroll" role="region" aria-label="Exemple fictif de comparaison des financements" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>Hypothèse</th>
                  <th>Décaissements sur 48 mois</th>
                  <th>Total</th>
                  <th>Situation finale</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Comptant</th>
                  <td>25 000 €</td>
                  <td>25 000 €</td>
                  <td>Voiture estimée 14 000 €</td>
                </tr>
                <tr>
                  <th>Crédit</th>
                  <td>5 000 € + 48 × 480 €</td>
                  <td>28 040 €</td>
                  <td>Voiture estimée 14 000 €</td>
                </tr>
                <tr>
                  <th>LOA rendue</th>
                  <td>4 000 € + 47 × 299 €</td>
                  <td>18 053 €</td>
                  <td>Aucune voiture</td>
                </tr>
                <tr>
                  <th>LOA rachetée</th>
                  <td>4 000 € + 47 × 299 € + 10 500 €</td>
                  <td>28 553 €</td>
                  <td>Voiture estimée 14 000 €</td>
                </tr>
                <tr>
                  <th>LLD</th>
                  <td>3 000 € + 47 × 329 €</td>
                  <td>18 463 €</td>
                  <td>Aucune voiture</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="as-article-caption">
            Hypothèse Auto Sens : valeur de revente de 14 000 € identique pour illustrer le calcul. Cette
            valeur est incertaine et ne constitue pas une prévision.
          </p>
          <p>
            La LOA à 299 € paraît la moins chère si l’on regarde la ligne publicitaire. Rachetée, elle dépasse
            pourtant le crédit dans cet exemple. Rendue, elle coûte moins en décaissements, mais ne laisse
            aucune voiture. Pour estimer le coût de détention, on soustrait la valeur finale hypothétique aux
            totaux des formules qui laissent le véhicule : 11 000 € au comptant, 14 040 € à crédit et 14 553 €
            en LOA rachetée. Ce résultat changerait avec les vraies offres et la vraie valeur de revente.
          </p>
        </>
      ),
    },
    {
      id: 'grille',
      title: 'La grille à remplir avant toute comparaison',
      content: (
        <>
          <ul className="as-article-checklist">
            <li>Prix comptant réellement négocié, avec les options et frais imposés.</li>
            <li>Somme versée le premier jour : apport, premier loyer ou dépôt de garantie.</li>
            <li>Nombre et montant de toutes les échéances.</li>
            <li>Coût total du crédit ou total des loyers en euros.</li>
            <li>Assurance emprunteur, entretien, assistance et pneus : inclus, optionnels ou absents.</li>
            <li>Kilométrage total contractuel et prix du dépassement.</li>
            <li>Prix de l’option d’achat en LOA, à la date prévue.</li>
            <li>Conditions de restitution et barème de remise en état.</li>
            <li>Coût d’une sortie anticipée, d’un sinistre total ou d’un vol.</li>
            <li>Valeur de revente prudente au même horizon pour l’achat ou le crédit.</li>
          </ul>
          <p>
            Je garde une colonne « certain », une colonne « estimation » et une colonne « risque ». Les loyers
            sont certains. La valeur de revente est une estimation. Les frais de restitution sont un risque à
            documenter. Additionner ces trois catégories sans les nommer donne un total faussement précis.
          </p>
        </>
      ),
    },
    {
      id: 'choix',
      title: 'Quelle formule regarder en premier ?',
      content: (
        <>
          <dl className="as-article-decisions">
            <div>
              <dt>Vous gardez vos voitures longtemps</dt>
              <dd>Achat comptant ou crédit, car la valeur résiduelle reste de votre côté.</dd>
            </div>
            <div>
              <dt>Vous voulez conserver votre épargne disponible</dt>
              <dd>Crédit à comparer avec le coût de cette liquidité et votre capacité de remboursement.</dd>
            </div>
            <div>
              <dt>Vous hésitez à garder la voiture</dt>
              <dd>LOA, si l’option et le coût de restitution sont lisibles et le kilométrage maîtrisé.</dd>
            </div>
            <div>
              <dt>Vous achetez un service d’usage, pas un patrimoine</dt>
              <dd>LLD, à condition que durée, kilomètres et services correspondent vraiment.</dd>
            </div>
          </dl>
          <aside className="as-article-note as-note-opinion">
            <strong>Mon avis</strong>
            <p>
              La formule la plus simple à comprendre mérite un avantage. Une offre légèrement plus chère mais
              transparente peut être préférable à un montage dont le coût dépend de plusieurs hypothèses
              favorables. Je ne transforme toutefois pas cette préférence de lisibilité en recommandation
              financière universelle.
            </p>
          </aside>
          <p>
            Le financement vient après le véhicule. Commencez par vérifier que le budget cible correspond
            au besoin avec les guides <a href="/conseils/quelle-citadine-15000-euros/">citadine à 15 000 €</a>
            et <a href="/conseils/quel-suv-30000-euros/">SUV à 30 000 €</a>, puis comparez les offres sur la
            même voiture et la même durée.
          </p>
        </>
      ),
    },
  ],
  sources: [
    {
      title: 'Location avec option d’achat d’un véhicule : règles à connaître',
      publisher: 'DGCCRF — Ministère de l’Économie',
      url: 'https://www.economie.gouv.fr/dgccrf/les-fiches-pratiques/location-avec-option-dachat-loa-dun-vehicule-quelles-sont-les-regles-connaitre',
    },
    {
      title: 'Crédit à la consommation : obligations de la banque',
      publisher: 'Service-Public.fr',
      url: 'https://www.service-public.fr/particuliers/vosdroits/F2440',
    },
    {
      title: 'Leasing automobile : fonctionnement et points de vigilance',
      publisher: 'Ministère de l’Économie',
      url: 'https://www.economie.gouv.fr/particuliers/gerer-mon-argent/gerer-mon-budget-et-mon-epargne/quest-ce-que-la-location-et-lachat',
    },
    {
      title: 'Location avec option d’achat : mode d’emploi',
      publisher: 'Institut national de la consommation',
      url: 'https://www.inc-conso.fr/content/location-avec-option-dachat-mode-demploi',
    },
  ],
  faqs: [
    {
      question: 'Qu’est-ce qui coûte le moins cher entre LOA et crédit ?',
      answer:
        'Il faut comparer les contrats réels. Pour garder la voiture, additionnez premier versement, loyers et option d’achat de la LOA, puis comparez ce total au montant total dû du crédit. Pour rendre la voiture, comparez un coût d’usage en intégrant kilométrage et restitution.',
    },
    {
      question: 'Le premier loyer majoré d’une LOA est-il récupéré ?',
      answer:
        'En principe non : il s’agit d’un loyer payé, contrairement à un éventuel dépôt de garantie. Le contrat doit préciser la nature de chaque somme et les conditions de restitution du dépôt.',
    },
    {
      question: 'Peut-on acheter la voiture à la fin d’une LLD ?',
      answer:
        'La LLD ne prévoit pas d’option d’achat contractuelle. Le loueur peut éventuellement proposer une vente séparée, mais elle n’est ni garantie ni fixée comme dans une LOA.',
    },
    {
      question: 'Pourquoi le TAEG n’apparaît-il pas sur une LOA ?',
      answer:
        'Le TAEG est l’indicateur prévu pour les crédits classiques ; Service-Public précise l’exception de la LOA. Pour une LOA, il faut donc lire le coût en euros : premier loyer, loyers, frais et option d’achat éventuelle.',
    },
  ],
  relatedSlugs: [
    'quel-suv-30000-euros',
    'quelle-citadine-15000-euros',
    'essence-diesel-hybride-electrique',
  ],
};
